/**
 * CPQ Application Entry Component
 *
 * Standalone React application for Configure, Price, Quote flows.
 * Customers are redirected to this app directly.
 */

import { Component } from 'react';
import { ToastContainer } from 'react-toastify';
import EntryRouter from '@router/EntryRouter';
import type { EntryRouterProps } from '@router/EntryRouter.types';

// ---------------------------------------------------------------------------
// Error Boundary
// ---------------------------------------------------------------------------

interface ErrorBoundaryState {
  hasError: boolean;
}

class AppErrorBoundary extends Component<React.PropsWithChildren, ErrorBoundaryState> {
  state: ErrorBoundaryState = { hasError: false };

  static getDerivedStateFromError(): ErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: unknown): void {
    console.error('Error rendering CPQ application:', error);
  }

  render() {
    if (this.state.hasError) {
      return <div>Unable to render application</div>;
    }
    return this.props.children;
  }
}

// ---------------------------------------------------------------------------
// App
// ---------------------------------------------------------------------------

type AppProps = EntryRouterProps & { shadowRoot?: ShadowRoot };

export default function App({ shadowRoot, ...routerProps }: AppProps) {
  return (
    <AppErrorBoundary>
      <EntryRouter {...routerProps} emotionContainer={shadowRoot} />
      <ToastContainer position="top-right" autoClose={5000} hideProgressBar newestOnTop closeOnClick />
    </AppErrorBoundary>
  );
}
